/**
 * Recovery from a stale build.
 *
 * Every page is a lazy chunk with a content hash in its name, and a deploy
 * replaces the whole asset folder. A tab opened before the deploy still runs
 * the old build, so the first visit to a page it has not loaded yet asks for a
 * chunk that is gone: the import rejects (or the server's SPA fallback answers
 * with `index.html`, which the browser refuses as a script). Only a reload,
 * which fetches the new `index.html`, gets the tab onto the current build.
 *
 * Tenant switching no longer reloads the page, so tabs live across deploys far
 * more often than they used to.
 */

/** How the browsers word a failed `import()` of a missing or non-JS chunk. */
const CHUNK_LOAD_PATTERNS = [
  /failed to fetch dynamically imported module/i, // Chromium
  /error loading dynamically imported module/i, // Firefox
  /importing a module script failed/i, // Safari
  /is not a valid javascript mime type/i, // SPA fallback served index.html
  /unable to preload css/i, // Vite's own preload helper
];

const RELOAD_STAMP_KEY = 'chunk-reload-at';

/** A second failure this soon after reloading means the reload did not help. */
const RELOAD_COOLDOWN_MS = 10_000;

export function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? '');

  return CHUNK_LOAD_PATTERNS.some(pattern => pattern.test(message));
}

/**
 * Reloads onto the current build, at most once per cooldown so a chunk that
 * is genuinely missing shows the error page instead of looping.
 *
 * Returns whether a reload was started.
 */
export function reloadForStaleChunk(now: number = Date.now()): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_STAMP_KEY) ?? 0);
    if (now - last < RELOAD_COOLDOWN_MS) return false;

    sessionStorage.setItem(RELOAD_STAMP_KEY, String(now));
  } catch {
    // No storage (private mode, blocked site data): without the stamp a loop
    // cannot be ruled out, so leave the error page up.
    return false;
  }

  window.location.reload();
  return true;
}
